<template>
  <PieChart
    ref="chart"
    :unit="unit"
    :scale="scale"
    :title="widgettitle"
    :loading="loading"
    :daterange="daterange"
  />
</template>

<script>
import { mapActions, mapState } from 'vuex';
import PieChart from '../PieChart.vue';
import {
  getDowntimeCategoryColor,
  summarizeDowntimeByDepartment,
} from '../../utils/downtime-colors';

export default {
  name: 'DowntimeDeptMonth',
  props: {
    widgettitle: { type: String, default: 'Dept 30 Days' },
    scale: { type: Number, default: 1 },
    unit: { type: String },
  },
  components: { PieChart },
  data() {
    return { loading: false, daterange: '' };
  },
  computed: {
    ...mapState('cellwiseDowntimeDashboard', [
      'shifts', 'serverTime', 'dataFromJSON', 'machines', 'downtimeReasons', 'currentDate', 'config', 'theme',
    ]),
    cellname() { return this.$route.query.selectedcell; },
  },
  watch: {
    cellname() { this.fetchData(); },
    currentDate(newVal, oldVal) {
      if (newVal && newVal !== oldVal && !this.dataFromJSON) this.fetchData();
    },
    'theme.isDark': function themeIsDark() {
      if (this.dataFromJSON) this.dummydata();
      else this.fetchData();
    },
  },
  mounted() {
    if (this.dataFromJSON) this.dummydata();
    else this.fetchData();
  },
  methods: {
    ...mapActions('cellwiseDowntimeDashboard', [
      'allShifts', 'getReports', 'getFileData', 'getMachines', 'fetchDowntimeReasons',
    ]),

    getSiteId() { return this.$store.getters['cellwiseDowntimeDashboard/activeSite']; },

    parseBusinessDate(yyyymmdd) {
      const dateStr = yyyymmdd.toString();
      const year = parseInt(dateStr.substring(0, 4), 10);
      const month = parseInt(dateStr.substring(4, 6), 10) - 1;
      const day = parseInt(dateStr.substring(6, 8), 10);
      return new Date(year, month, day);
    },

    getYYYYMMDD(dateObj) {
      return parseInt(
        `${dateObj.getFullYear()}${String(dateObj.getMonth() + 1).padStart(2, '0')}${String(dateObj.getDate()).padStart(2, '0')}`,
        10,
      );
    },

    async dummydata() {
      const res = await this.getFileData('jsondata/depttmonth.json');
      const baseTime = this.serverTime ? new Date(this.serverTime).setHours(0, 0, 0, 0)
        : new Date().setHours(0, 0, 0, 0);
      const endTs = baseTime - 86400000;
      const startTs = endTs - (29 * 86400000);
      this.setTitle(startTs, endTs);
      if (this.$refs.chart && res) this.$refs.chart.assignData(res);
    },

    async fetchData() {
      if (this.loading) return;
      this.loading = true;

      try {
        if (!this.currentDate) {
          this.setZeroState();
          return;
        }

        const endDateObj = this.parseBusinessDate(this.currentDate);
        endDateObj.setDate(endDateObj.getDate() - 1);

        const startDateObj = new Date(endDateObj.getTime());
        startDateObj.setDate(startDateObj.getDate() - 29);

        const endYYYYMMDD = this.getYYYYMMDD(endDateObj);
        const startYYYYMMDD = this.getYYYYMMDD(startDateObj);

        this.setTitle(startDateObj.getTime(), endDateObj.getTime());

        const activeSite = this.getSiteId();
        if (!activeSite) {
          this.setZeroState();
          return;
        }

        const payload = {
          start: startYYYYMMDD,
          end: endYYYYMMDD,
          siteid: activeSite,
        };

        const reportName = 'dailymachinedowntime';
        const result = await this.getReports({ reportName, payload });

        if (result) {
          let parsed = typeof result === 'string' ? JSON.parse(result) : result;
          if (typeof parsed === 'string') {
            try { parsed = JSON.parse(parsed); } catch (e) { console.warn(e); }
          }

          let actualArray = null;
          if (Array.isArray(parsed)) {
            actualArray = parsed;
          } else if (parsed && Array.isArray(parsed.reportData)) {
            actualArray = parsed.reportData;
          } else if (parsed && Array.isArray(parsed.data)) {
            actualArray = parsed.data;
          }

          if (actualArray && actualArray.length > 0 && this.cellname) {
            if (!this.machines || this.machines.length === 0) await this.getMachines();
            let validMachineNames = [];
            if (this.machines && this.machines.length > 0) {
              validMachineNames = this.machines
                .filter((m) => m.machinecell === this.cellname)
                .map((m) => m.machinename);
            }
            if (validMachineNames.length > 0) {
              actualArray = actualArray.filter((row) => {
                const mName = row.machinename || row.machine || row.assetname || '';
                return validMachineNames.includes(mName);
              });
            } else {
              actualArray = [];
            }
          }

          let chartData = null;
          if (actualArray && actualArray.length > 0) {
            if (!this.downtimeReasons || this.downtimeReasons.length === 0) {
              if (typeof this.fetchDowntimeReasons === 'function') {
                try { await this.fetchDowntimeReasons(); } catch (e) { /* Ignore error */ }
              }
            }

            const safeReasons = Array.isArray(this.downtimeReasons) ? this.downtimeReasons : [];
            const departments = summarizeDowntimeByDepartment(
              actualArray,
              safeReasons,
              6,
            );
            const departmentOrder = departments.map((dept) => dept.name);
            const totalDowntime = departments.reduce((sum, item) => (
              sum + (Number(item.total) || 0)
            ), 0);

            const dataArr = [];
            const activeTheme = this.$vuetify.theme.dark ? 'dark' : 'light';

            departments.forEach((dept) => {
              const percentage = totalDowntime > 0 ? (Number(dept.total) / totalDowntime) * 100 : 0;
              dataArr.push({
                name: dept.name === 'other' ? 'Other' : dept.name,
                y: Number(percentage.toFixed(1)),
                color: getDowntimeCategoryColor(
                  this.config,
                  dept.name,
                  activeTheme,
                  departmentOrder,
                ),
              });
            });

            if (dataArr.length > 0) chartData = { series: [{ name: 'Downtime', data: dataArr }] };
          }

          if (this.$refs.chart && chartData) this.$refs.chart.assignData(chartData);
          else this.setZeroState();
        } else this.setZeroState();
      } catch (e) {
        this.setZeroState();
      } finally {
        this.loading = false;
      }
    },

    setZeroState() {
      if (this.$refs.chart) this.$refs.chart.assignData({ series: [{ name: 'Downtime', data: [] }] });
    },

    setTitle(start, end) {
      if (start && end && start !== end) this.daterange = `${this.dateFormat(start)} - ${this.dateFormat(end)}`;
      else this.daterange = this.dateFormat(start);
    },

    dateFormat(val) {
      if (!val) return '';
      try {
        const date = new Date(val);
        if (Number.isNaN(date.getTime())) return '';
        const day = new Intl.DateTimeFormat('en', { day: '2-digit' }).format(date);
        const year = new Intl.DateTimeFormat('en', { year: 'numeric' }).format(date);
        const month = new Intl.DateTimeFormat('en', { month: '2-digit' }).format(date);
        return `${day}/${month}/${year}`;
      } catch (e) { return ''; }
    },
  },
};
</script>
